import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '../components/ui/popover';
import { 
    Mail, 
    Building2, 
    DollarSign, 
    FileCheck,
    CalendarIcon,
    ChevronDown,
    ChevronUp,
    Loader2,
    RefreshCw,
    Trash2, Sparkles } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toast } from 'sonner';
import { toastDeletedWithUndo } from '../lib/undoDelete';

const API = `/api`;

// Bank icon colors
const BANK_COLORS = {
    'Extras': 'bg-fuchsia-600',
    'TD Bank': 'bg-green-500',
    'Capital One': 'bg-red-500',
    'Cash App': 'bg-emerald-500',
    'PayPal': 'bg-blue-600',
    'Zelle': 'bg-purple-500',
    'Wise': 'bg-green-600',
    'Bank of America': 'bg-red-600',
    'Chase': 'bg-blue-800',
    'PNC Bank': 'bg-orange-500',
    'Regions Bank': 'bg-green-600',
    'Zelle Generic': 'bg-purple-400',
    'Desconocido': 'bg-slate-400'
};

const BankCard = ({ bank, bankName, isExtra, count, totalAmount, verifications, formatCurrency, autoCount, manualCount, isAdmin, onDelete }) => {
    const [expanded, setExpanded] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const displayName = bankName || bank;
    const bgColor = BANK_COLORS[displayName] || 'bg-slate-500';
    
    const handleDelete = async (e, activityId) => {
        e.stopPropagation();
        if (!window.confirm('¿Estás seguro de eliminar esta factura del sistema? Se eliminará la factura y todos sus registros.')) return;
        
        setDeletingId(activityId);
        try {
            await onDelete(activityId);
        } finally {
            setDeletingId(null);
        }
    };
    
    return (
        <Card className={`border-0 shadow-sm overflow-hidden ${isExtra ? 'ring-1 ring-fuchsia-200' : ''}`} data-testid={`bank-card-${bank.replace(/\s+/g, '-').toLowerCase()}`}>
            <div 
                className="cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-y-2">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center shrink-0`}>
                                <Building2 className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                    {displayName}
                                    {isExtra && (
                                        <span className="text-xs bg-fuchsia-100 text-fuchsia-700 px-1.5 py-0.5 rounded font-medium" data-testid="bank-card-extra-badge">
                                            Extra
                                        </span>
                                    )}
                                </h3>
                                <div className="flex items-center gap-2 text-sm text-slate-500 flex-wrap">
                                    <span>{count} verificacion{count !== 1 ? 'es' : ''}</span>
                                    {autoCount > 0 && (
                                        <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded">
                                            {autoCount} auto
                                        </span>
                                    )}
                                    {manualCount > 0 && (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded">
                                            {manualCount} manual
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <p className="text-lg font-bold text-green-600">{formatCurrency(totalAmount)}</p>
                            </div>
                            {expanded ? (
                                <ChevronUp className="w-5 h-5 text-slate-400" />
                            ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                            )}
                        </div>
                    </div>
                </CardContent>
            </div>
            
            {expanded && verifications.length > 0 && (
                <div className="border-t border-slate-100 bg-slate-50 p-4">
                    <div className="space-y-2">
                        {verifications.map((v, idx) => (
                            <div key={v.id || idx} className="bg-white rounded-lg p-3 flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                    {v.is_manual ? (
                                        <span className="text-xs bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded" title="Verificación manual">
                                            M
                                        </span>
                                    ) : (
                                        <span className="text-xs bg-purple-100 text-purple-600 px-1.5 py-0.5 rounded" title="Verificación automática">
                                            A
                                        </span>
                                    )}
                                    <div>
                                        <p className="font-medium text-slate-700">{v.invoice_name}</p>
                                        <p className="text-slate-500 text-xs">{v.invoice_user_name}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <div className="text-right">
                                        <p className="font-semibold text-slate-800">{formatCurrency(v.amount)}</p>
                                        <p className="text-slate-400 text-xs">
                                            {new Date(v.created_at).toLocaleTimeString('es-ES', { 
                                                hour: '2-digit', 
                                                minute: '2-digit' 
                                            })}
                                        </p>
                                    </div>
                                    {isAdmin && (
                                        <button
                                            onClick={(e) => handleDelete(e, v.id)}
                                            disabled={deletingId === v.id}
                                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                                            title="Eliminar registro"
                                            data-testid={`delete-extraction-${v.id}`}
                                        >
                                            {deletingId === v.id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
};

export default function EmailExtractions() {
    const { user, getAuthHeaders } = useAuth();
    const [loading, setLoading] = useState(true);
    const [date, setDate] = useState(new Date());
    const [data, setData] = useState(null);

    const isAdmin = user?.role === 'admin';

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-ES', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const handleDeleteExtraction = async (activityId) => {
        try {
            const res = await axios.delete(`${API}/activity-log/${activityId}`, {
                headers: getAuthHeaders()
            });
            if (res.data?.can_undo && res.data?.invoice_id) {
                toastDeletedWithUndo({ invoiceId: res.data.invoice_id, headers: getAuthHeaders(), onRestored: () => fetchData(date) });
            } else {
                toast.success('Registro eliminado');
            }
            // Refresh data
            fetchData(date);
        } catch (error) {
            console.error('Error deleting extraction:', error);
            toast.error('Error al eliminar la factura');
        }
    };

    const fetchData = async (selectedDate) => {
        setLoading(true);
        try {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const response = await axios.get(`${API}/email-extractions?date=${dateStr}`, {
                headers: getAuthHeaders()
            });
            setData(response.data);
        } catch (error) {
            console.error('Error fetching email extractions:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(date);
    }, []);

    const handleDateChange = (newDate) => {
        if (newDate) {
            setDate(newDate);
            fetchData(newDate);
        }
    };

    const handleRefresh = () => {
        fetchData(date);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
            </div>
        );
    }

    const regularBanks = (data?.by_bank || []).filter(b => !b.is_extra);
    const extraBanks = (data?.by_bank || []).filter(b => b.is_extra);

    return (
        <div className="space-y-6 animate-fade-in" data-testid="email-extractions-page">
            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-2">
                        <Mail className="w-7 h-7 text-sky-500" />
                        Extracciones de Correos
                    </h1>
                    <p className="text-slate-500 mt-1">
                        Verificaciones automáticas y manuales por banco
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="w-[200px] justify-start text-left font-normal"
                                data-testid="extractions-date-btn"
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {format(date, "dd 'de' MMMM, yyyy", { locale: es })}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="end">
                            <Calendar
                                mode="single"
                                selected={date}
                                onSelect={handleDateChange}
                                locale={es}
                                defaultMonth={date}
                                initialFocus
                            />
                        </PopoverContent>
                    </Popover>
                    <Button
                        variant="outline"
                        size="icon"
                        onClick={handleRefresh}
                        className="border-sky-200 hover:bg-sky-50"
                        data-testid="refresh-extractions-btn"
                    >
                        <RefreshCw className="w-4 h-4 text-sky-600" />
                    </Button>
                </div>
            </div>

            {/* Summary Cards — regular users vs extra users, always separate */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm bg-gradient-to-br from-sky-50 to-white">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center">
                                <Mail className="w-6 h-6 text-sky-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Extracciones (regulares)</p>
                                <p className="text-2xl font-bold text-sky-600" data-testid="extractions-regular-count">
                                    {data?.total_extractions || 0}
                                </p>
                                {data?.extra_extractions > 0 && (
                                    <p className="text-xs text-fuchsia-600">+ {data.extra_extractions} extra</p>
                                )}
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                                <DollarSign className="w-6 h-6 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Monto Total (regulares)</p>
                                <p className="text-2xl font-bold text-green-600" data-testid="extractions-regular-amount">
                                    {formatCurrency(data?.total_amount || 0)}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-gradient-to-br from-fuchsia-50 to-white">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-fuchsia-100 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-fuchsia-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Monto Extra (usuarios extra)</p>
                                <p className="text-2xl font-bold text-fuchsia-600" data-testid="extractions-extra-amount">
                                    {formatCurrency(data?.extra_amount || 0)}
                                </p>
                                <p className="text-xs text-slate-400">{data?.extra_extractions || 0} extracciones · no suma al total regular</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="border-0 shadow-sm bg-gradient-to-br from-purple-50 to-white">
                    <CardContent className="p-6">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                                <Building2 className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">Bancos Activos</p>
                                <p className="text-2xl font-bold text-purple-600">
                                    {data?.by_bank?.filter(b => !b.is_extra).length || 0}
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Bank List */}
            <div>
                <h2 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-sky-500" />
                    Desglose por Banco
                    <span className="text-xs font-normal text-slate-400">(usuarios regulares)</span>
                </h2>
                
                {regularBanks.length > 0 ? (
                    <div className="space-y-3" data-testid="regular-banks-list">
                        {regularBanks.map((bank) => (
                            <BankCard
                                key={bank.bank}
                                bank={bank.bank}
                                bankName={bank.bank_name}
                                isExtra={false}
                                count={bank.count}
                                totalAmount={bank.total_amount}
                                verifications={bank.verifications}
                                formatCurrency={formatCurrency}
                                autoCount={bank.auto_count || 0}
                                manualCount={bank.manual_count || 0}
                                isAdmin={isAdmin}
                                onDelete={handleDeleteExtraction}
                            />
                        ))}
                    </div>
                ) : (
                    <Card className="border-0 shadow-sm">
                        <CardContent className="py-16 text-center">
                            <Mail className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-lg font-medium text-slate-600">
                                No hay extracciones este día
                            </p>
                            <p className="text-slate-400 mt-1">
                                Las verificaciones automáticas aparecerán aquí
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Extra users - separate accounting, still grouped by bank */}
            {extraBanks.length > 0 && (
                <div className="rounded-xl border border-fuchsia-200 bg-fuchsia-50/50 p-4" data-testid="extra-banks-section">
                    <h2 className="text-lg font-semibold text-fuchsia-800 mb-1 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-fuchsia-600" />
                        Usuarios Extra (Aparte) · por Banco
                    </h2>
                    <p className="text-xs text-fuchsia-700/70 mb-4">
                        Contabilidad separada · {data.extra_extractions} extracciones · {formatCurrency(data.extra_amount || 0)} · no suma al total regular
                    </p>
                    <div className="space-y-3" data-testid="extra-banks-list">
                        {extraBanks.map((bank) => (
                            <BankCard
                                key={bank.bank}
                                bank={bank.bank}
                                bankName={bank.bank_name}
                                isExtra
                                count={bank.count}
                                totalAmount={bank.total_amount}
                                verifications={bank.verifications}
                                formatCurrency={formatCurrency}
                                autoCount={bank.auto_count || 0}
                                manualCount={bank.manual_count || 0}
                                isAdmin={isAdmin}
                                onDelete={handleDeleteExtraction}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
